import React, { useCallback, useEffect } from 'react';
import { Container, Paper, Typography, Box, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { parseQueryParams, buildSearchQueryParams } from 'util/query-params-util';
import { useLocation, useNavigate, useParams } from 'react-router';
import { AppStoreState } from 'types';
import { useSelector, useDispatch } from 'react-redux';
import { HwtButton } from 'components/@common/Buttons/HwtButton';
import { StepOneAssignmentAlerts } from './StepOneAssignmentAlerts';
import { calcDuration } from 'util/assignment-util';
import {
  deleteUserAssignment,
  getAssignment,
  getUserAssignmentsView
} from 'state/modules/entity/thunks/assignment';
import { useAlert, AlertVariant } from 'state/providers/AlertProvider';
import { UnassignLessonModal } from 'components/@common/Modals/UnassignLessonModal';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import { BaseModalTitle } from 'components/@common/Modals/BaseModalTitle';
import { translate } from 'translations/translator';
import { AssignmentEngineParams } from 'state/providers/LessonAssignmentProvider/types';
import {
  hasStartedOrCompletedAttempts,
  getStartedAndCompletedAttempts,
  isReleasedToStudents
} from 'util/lesson-plan-util';
import { EditAssignmentModal } from 'components/@common/Modals/EditAssignmentModal';
import { createEvent } from 'api';
import { fetchUserClassrooms } from 'state/modules/entity/custom/classrooms/thunks';
import { getEpochTime, formatUiDate, isEqualDate } from 'util/date-util';
import CommonTranslationVariant from 'translations/variants/common';
import LessonAssignmentTranslationVariant from 'translations/variants/lessonAssignment';

const translateAssignment = key => translate('lessonAssignment', key);
const translateLessonPlanner = key => translate('lessonPlanner', key);
const translateCommon = (key: CommonTranslationVariant) => translate('common', key);

export const ViewAssignments = () => {
  const theme = useTheme();
  const classes = useStyles(theme)();
  const location = useLocation();
  const dispatch = useDispatch() as any;
  const navigate = useNavigate();
  const { bookCode, edition } = useParams();
  const [, { openAlert }] = useAlert();
  const [, { openModal }] = useModal();
  const { epochTime }: any = parseQueryParams(location.search) ?? {};
  const workbook = useSelector(({ app }: AppStoreState) => app.currentWorkbook);
  const classrooms = useSelector(({ entity }: AppStoreState) => entity.classrooms);
  const allAssignments = useSelector(({ entity }: AppStoreState) => entity.userAssignments);
  const [userAssignments, setUserAssignments] = React.useState<any>(null);

  const maybeFetchOninit = useCallback(async () => {
    if (!classrooms.length) await dispatch(fetchUserClassrooms());
    if (!allAssignments?.length) await dispatch(getUserAssignmentsView());
  }, [dispatch, classrooms.length, allAssignments]);

  /* Run once on mount */
  useEffect(() => {
    maybeFetchOninit();
    // eslint-disable-next-line
  }, []);

  const getAllAssignments = useCallback(() => {
    const paramDate = new Date(Number.parseInt(epochTime, 10));
    const { assignments }: any =
      allAssignments?.find(({ date }) => isEqualDate(new Date(date), paramDate)) ?? {};

    return (
      assignments?.reduce((acc, current) => {
        const result = [
          {
            ...current,
            classroom: classrooms.find(c => c.id === current.classroomId) ?? classrooms[0]
          }
        ];
        return [...acc, ...result];
      }, []) ?? []
    );
  }, [classrooms, allAssignments, epochTime]);

  useEffect(() => {
    if (classrooms.length && allAssignments?.length) {
      const assignments = getAllAssignments();
      setUserAssignments(assignments);
    }
  }, [classrooms, allAssignments, getAllAssignments]);

  const getAttempts = useCallback(
    async (assignmentId: number) => {
      const {
        data: { assignmentDetail }
      } = await dispatch(getAssignment(assignmentId, { includeAttempts: true }));

      return assignmentDetail.attempts;
    },
    [dispatch]
  );

  const handleUnassign = useCallback(
    async (
      { id: assignmentId, classroomId, studentIds, teachingOrderLessonId, dateAssigned, dateDue },
      { started, completed },
      params
    ) => {
      await dispatch(deleteUserAssignment(assignmentId, params));

      createEvent('assignmentDeleted', {
        assignmentId,
        classroomId,
        studentIds,
        teachingOrderLessonId,
        dateAssigned,
        dateDue,
        ...((started || completed) && { attempts: { started, completed } }),
        ...params
      });

      openAlert({
        body: translateAssignment(LessonAssignmentTranslationVariant.unassignSuccessful),
        variant: AlertVariant.SUCCESS
      });

      navigate(-1);
    },
    [dispatch, openAlert]
  );

  const handleEdit = useCallback(
    assignment => async () => {
      const attempts = await getAttempts(assignment.id);

      if (hasStartedOrCompletedAttempts(attempts)) {
        const startedAndCompleted = getStartedAndCompletedAttempts(attempts);
        openModal({
          Title: () => <BaseModalTitle text={translateLessonPlanner('editAssignment')} />,
          body: (
            <EditAssignmentModal
              onSubmit={() => handleUnassign(assignment, startedAndCompleted, { force: true })}
            />
          )
        });
        return;
      }

      const [{ productCurriculumId }] = workbook.products;
      const epochTime = getEpochTime(assignment.dateDue);
      navigate({
        pathname: `/lesson-assignment/${bookCode}/${edition}/${productCurriculumId}/${epochTime}`,
        search: buildSearchQueryParams({
          assignment: AssignmentEngineParams.EDIT,
          assignmentId: assignment.id
        })
      });
    },
    [bookCode, edition, workbook, getAttempts, handleUnassign, openModal]
  );

  const openUnassignModal = assignment => async () => {
    const attempts = await getAttempts(assignment.id);
    const { started, completed } = getStartedAndCompletedAttempts(attempts);
    const assignmentReleased = isReleasedToStudents(assignment);
    const params = {
      ...((assignmentReleased || started || completed) && { force: true })
    };

    openModal({
      Title: () => <BaseModalTitle text={translateAssignment('unassignLesson')} />,
      body: (
        <UnassignLessonModal
          handleUnassign={() => handleUnassign(assignment, { started, completed }, params)}
          started={started}
          completed={completed}
        />
      )
    });
  };

  return (
    <Paper className={classes.main} style={{ display: 'flex', justifyContent: 'center' }}>
      <Box style={{ margin: '15px 55px', width: '82vw', alignSelf: 'center' }}>
        <Typography variant="h4" style={{ fontWeight: 500 }}>
          {`${translateAssignment('assignmentsFor')} `}
          {userAssignments?.length > 0 && formatUiDate(new Date(userAssignments[0].dateDue))}
        </Typography>
      </Box>
      <Container className={classes.outerLessonContainer}>
        {userAssignments?.map((a, assignmentIdx) => (
          <Container
            key={`assignment-${assignmentIdx}`}
            className={classes.lessonContainer}
            style={{ padding: 32 }}
          >
            <Box display="flex" flexDirection="column">
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignContent="center"
              >
                <Typography variant="h6" className={classes.assignmentTitle}>
                  {a.title}
                </Typography>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <HwtButton onClick={handleEdit(a)} variant="outlined" style={{ marginBottom: 0 }}>
                    <Typography>
                      {`${translateCommon(CommonTranslationVariant.edit)} ${translateAssignment(
                        'assignment'
                      )}`}
                    </Typography>
                  </HwtButton>
                  <HwtButton
                    onClick={openUnassignModal(a)}
                    variant="text"
                    style={{ marginBottom: 0, height: '48px', textTransform: 'none' }}
                  >
                    <Typography>{translateAssignment('unassign')}</Typography>
                  </HwtButton>
                </Box>
              </Box>
              <Box display="flex" flexDirection="row">
                <span style={{ display: 'flex', marginRight: 5 }}>
                  <Typography variant="subtitle2" style={{ marginRight: 5, fontWeight: 'bold' }}>
                    {translateAssignment('assignedTo')}:
                  </Typography>
                  <Typography
                    className={classes.fontWeight}
                    variant="subtitle2"
                    style={{ marginRight: 5 }}
                  >
                    {a.classroom.name}
                  </Typography>
                </span>
                <span style={{ display: 'flex', marginRight: 5 }}>
                  <Typography variant="subtitle2" style={{ marginRight: 5, fontWeight: 'bold' }}>
                    {translateAssignment('dueDate')}:
                  </Typography>
                  <Typography variant="subtitle2" className={classes.fontWeight}>
                    {formatUiDate(new Date(a.dateDue))}
                  </Typography>
                  {a.instructions && (
                    <Box display="flex" flexDirection="row" style={{ margin: '0 5px' }}>
                      <Typography
                        variant="subtitle2"
                        className={classes.fontWeight}
                        style={{ fontWeight: 'bold' }}
                      >
                        {translateAssignment('teachingInstructions')}:
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        className={classes.fontWeight}
                        style={{ marginLeft: 5 }}
                      >
                        {a.instructions}
                      </Typography>
                    </Box>
                  )}
                </span>
              </Box>
              <Typography variant="h5" style={{ fontWeight: 'bolder' }}>
                {a.sections.title}
              </Typography>
            </Box>
            {a?.sections?.map((s, sectionIdx) => (
              <Container
                key={`assignment-${assignmentIdx}-section-${sectionIdx}`}
                className={classes.lessonContainer}
                style={{ padding: 32 }}
              >
                <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: 5 }}>
                  {s.title}
                </Typography>
                {s.activities.map((a, activityIdx) => (
                  <Box
                    key={`assignment-${assignmentIdx}-section-${sectionIdx}-activity-${activityIdx}`}
                    display="flex"
                    flexDirection="column"
                    style={{ margin: '5px 0px' }}
                  >
                    <Typography className={classes.fontWeight}>{a.title}</Typography>
                    <Typography style={{ color: '#6A6A6A' }}>
                      {translateAssignment('activityType')}: {a.type.title}
                    </Typography>
                  </Box>
                ))}
              </Container>
            ))}
            <Box display="flex">
              <StepOneAssignmentAlerts
                style={{ margin: '27px 0px 0px' }}
                type={'duration'}
                duration={calcDuration({ lesson: a })}
              />
            </Box>
          </Container>
        ))}
      </Container>
    </Paper>
  );
};

const useStyles = theme =>
  makeStyles(() => ({
    main: () => ({
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: `${theme.palette.primary.light} !important`,
      flex: 1,
      maxWidth: '1820px',
      padding: 0,
      minHeight: '100%'
    }),
    outerLessonContainer: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      margin: '0px 55px 15px 55px',
      alignSelf: 'center',
      width: '82vw !important',
      maxWidth: '1356px !important',
      borderRadius: '5px',
      backgroundColor: '#fff',
      boxShadow:
        '0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)'
    },
    lessonContainer: {
      border: '1px solid #DBDBDB',
      borderRadius: 5,
      margin: '24px 0px'
    },
    lessonTitle: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      margin: '30px 0px'
    },
    fontWeight: {
      fontWeight: 300
    },
    assignmentTitle: {
      display: 'flex',
      alignContent: 'center',
      alignItems: 'center',
      justifyContent: 'center'
    }
  }));
